// Per-session KEK_admin_ti management (D-202, Phase 2.2 ADM-01).
//
// Paridade RIGOROSA com apps/web-comite/src/lib/kek-member-session.ts.
// Decisão de copiar (não compartilhar pacote) per RESEARCH §KEK_admin_ti
// Derivation — web-admin e web-comite têm bundles Vite independentes;
// duplicação pequena é menos risk que monorepo refactor.
//
// STORAGE_KEY diferente para evitar collision em browser que tenha ambos
// SPAs em mesmo origin (não acontece em produção — origins distintos —
// mas defesa em depth).
//
// SECURITY: KEK plaintext NEVER hits sessionStorage. Only AES-GCM ciphertext.
//
// ⚠ fix 02.8-05 (paridade comitê): a chave de storage deixou de ser HKDF(JWT)
// e passou a ser um SEGREDO de sessão NÃO-EXTRAÍVEL em memória, independente do
// token. O admin NÃO tinha o bug ativo (sem liveness/sliding-refresh → token
// estável), mas usava a MESMA mina JWT-bound — este fix é hardening simétrico.
// Hard-refresh → re-login (segredo memory-only). Doc: docs/bugs/2026-06-22-*.

import { deriveKeyMaterial, splitKeyMaterial } from '@aions/crypto';
import { bytesToBase64, base64ToBytes } from './bytes';

const STORAGE_KEY = 'aions:admin:kek_admin_ti_encrypted:v2';

let memKekEncrypted: string | null = null;

// Segredo de sessão (fix 02.8-05): CryptoKey AES-GCM NÃO-EXTRAÍVEL, em memória
// module-level, gerado no 1º store, independente do token. Zerado em clearKekAdminTi.
let sessionKey: CryptoKey | null = null;

async function ensureSessionKey(): Promise<CryptoKey> {
  if (!sessionKey) {
    sessionKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
      'encrypt',
      'decrypt',
    ]);
  }
  return sessionKey;
}

function readStorage(): string | null {
  if (memKekEncrypted) return memKekEncrypted;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) memKekEncrypted = raw;
    return raw;
  } catch {
    return null;
  }
}

function writeStorage(payload: string): void {
  memKekEncrypted = payload;
  try {
    sessionStorage.setItem(STORAGE_KEY, payload);
  } catch {
    // Safari incognito quota — keep memory copy only.
  }
}

function removeStorage(): void {
  memKekEncrypted = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Derives 32-byte KEK_admin_ti from password + salt via Argon2id
 * (paridade Phase 2.1 D-190). Salt é kekSalt do UserRecord do admin.
 */
export async function deriveKekAdminTi(password: string, kekSaltB64: string): Promise<Uint8Array> {
  const material = await deriveKeyMaterial(kekSaltB64, password);
  const { kekRaw } = splitKeyMaterial(material);
  return kekRaw;
}

/**
 * Encrypts kekRaw with AES-GCM(key=HKDF(jwtToken)) and stores in
 * sessionStorage (with in-mem fallback).
 */
export async function storeKekAdminTi(kekRaw: Uint8Array): Promise<void> {
  const key = await ensureSessionKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new Uint8Array(kekRaw));
  const combined = new Uint8Array(12 + ct.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ct), 12);
  writeStorage(bytesToBase64(combined));
}

/**
 * Decrypts and returns the 32-byte KEK_admin_ti from sessionStorage.
 * Throws `kek-admin-session: no_kek_in_session` if absent OU se o segredo de
 * sessão foi perdido (hard-refresh — re-login). `kek-admin-session:
 * kek_decrypt_failed` se o ciphertext não abre com o segredo atual.
 */
export async function loadKekAdminTi(): Promise<Uint8Array> {
  const stored = readStorage();
  if (!stored) throw new Error('kek-admin-session: no_kek_in_session');
  if (!sessionKey) throw new Error('kek-admin-session: no_kek_in_session');
  const combined = base64ToBytes(stored);
  const iv = combined.slice(0, 12);
  const ct = combined.slice(12);
  try {
    const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, sessionKey, ct);
    return new Uint8Array(pt);
  } catch {
    throw new Error('kek-admin-session: kek_decrypt_failed');
  }
}

/** Clears the in-memory cache, sessionStorage AND the session secret
 *  (logout/expiry; fix 02.8-05 — sem o segredo o ciphertext residual é inerte). */
export function clearKekAdminTi(): void {
  sessionKey = null;
  removeStorage();
}
